/* eslint-disable @next/next/no-img-element */
"use client";

import Link from "next/link";
import { useState, useEffect } from "react";
import { X, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { RiMenu3Line } from "react-icons/ri";
interface DropdownItem {
  label: string;
  href: string;
}

interface NavItem {
  label: string;
  href?: string;
  dropdown?: DropdownItem[];
}

interface NavbarProps {
  defaultLogo?: string; // before scroll
  scrolledLogo?: string; // after scroll
  navItems?: NavItem[];
  isLight?: boolean;

  // 🎨 Colors
  labelColor?: string;
  labelHoverColor?: string;
  buttonBg?: string;
  buttonTextColor?: string;
  buttonHoverBg?: string;
  buttonHoverText?: string;

  // CTA Button
  ctaText?: string;
  ctaLink?: string;
}

export default function Navbar({
  defaultLogo = "/logo-white.png",
  scrolledLogo = "/logo-dark.png",
  navItems = [
    { label: "Home", href: "/" },
    {
      label: "Build Your Buying Power",
      href: "/askLocal-digital-mortgage-coach",
    },
    {
      label: "Services",
      dropdown: [
        { label: "Buyers", href: "/buyers" },
        { label: "Sellers", href: "/sellers" },
        { label: "Investors", href: "/investors" },
      ],
    },
    { label: "Agents", href: "/agents" },
    { label: "About", href: "/about" },
  ],
  isLight = false,

  // Default Colors
  labelColor = "#000000",
  labelHoverColor = "#EB9C2C",
  buttonBg = "#EB9C2C",
  buttonTextColor = "#ffffff",
  buttonHoverBg = "#227abb",
  buttonHoverText = "#ffffff",

  // CTA
  ctaText = "Become a Partner",
  ctaLink = "/match",
}: NavbarProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  // ✅ Detect scroll
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  // useEffect(() => {
  //   const body = document.body;

  //   if (isMenuOpen) {
  //     // Save the current scroll position
  //     const scrollY = window.scrollY;
  //     body.style.position = "fixed";
  //     body.style.top = `-${scrollY}px`;
  //     body.style.left = "0";
  //     body.style.right = "0";
  //     body.style.overflowY = "hidden";
  //     body.style.width = "100%";
  //   } else {
  //     // Restore scroll position
  //     const scrollY = body.style.top;
  //     body.style.position = "";
  //     body.style.top = "";
  //     body.style.left = "";
  //     body.style.right = "";
  //     body.style.overflowY = "";
  //     body.style.width = "";
  //     window.scrollTo(0, parseInt(scrollY || "0") * -1);
  //   }

  //   return () => {
  //     body.style.position = "";
  //     body.style.top = "";
  //     body.style.left = "";
  //     body.style.right = "";
  //     body.style.overflowY = "";
  //     body.style.width = "";
  //   };
  // }, [isMenuOpen]);

  // ✅ Navbar background & color logic
  const headerBg = isScrolled
    ? "bg-white/90 shadow-md backdrop-blur-md border-b border-gray-100"
    : "bg-transparent";

  const currentLabelColor = isScrolled
    ? "#000000"
    : isLight
    ? "#ffffff"
    : labelColor;

  const currentHoverColor = isScrolled
    ? "#227abb"
    : isLight
    ? "#EB9C2C"
    : labelHoverColor;

  const currentButtonBg = isScrolled ? "#EB9C2C" : buttonBg;
  const currentButtonText = isScrolled ? "#ffffff" : buttonTextColor;
  const currentButtonHoverBg = isScrolled ? "#227abb" : buttonHoverBg;
  const currentButtonHoverText = isScrolled ? "#ffffff" : buttonHoverText;

  // ✅ Width control (shrink on scroll, expand on hover)
  const defaultWidth = "1290px";
  const scrolledWidth = "1100px";

  return (
    <header
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${headerBg}`}
    >
      <motion.div
        layout
        animate={{
          width: isScrolled
            ? isHovered
              ? defaultWidth // expand back to full when hover
              : scrolledWidth // shrink on scroll
            : defaultWidth, // default width
        }}
        transition={{ duration: 0.6, ease: "easeInOut" }}
        className="mx-auto px-4 md:px-8 py-4 flex items-center justify-between relative"
        style={{ maxWidth: "100%" }}
      >
        {/* ---------- Logo ---------- */}
        <motion.div>
          <Link href="/" className="transition-transform duration-500">
            <img
              className={`transition-all duration-700 ${
                isScrolled ? "w-32" : "w-44"
              }`}
              src={isScrolled ? scrolledLogo : defaultLogo}
              alt="logo"
            />
          </Link>
        </motion.div>

        {/* ---------- Desktop Menu ---------- */}
        <nav className="hidden md:flex space-x-8 items-center">
          {navItems.map((item) =>
            item.dropdown ? (
              <div
                key={item.label}
                className="relative group"
                onMouseEnter={() => setOpenDropdown(item.label)}
                onMouseLeave={() => setOpenDropdown(null)}
              >
                <button
                  className="flex items-center gap-1 text-sm font-semibold transition-all"
                  style={{ color: currentLabelColor }}
                >
                  {item.label}
                  <ChevronDown size={16} />
                </button>

                {/* --- Modern Dropdown --- */}
                <AnimatePresence>
                  {openDropdown === item.label && (
                    <motion.div
                      initial={{ opacity: 0, y: -8, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -8, scale: 0.95 }}
                      transition={{ duration: 0.25, ease: "easeOut" }}
                      className="absolute left-0 top-full mt-3 w-64 bg-white/95 backdrop-blur-md shadow-[0_8px_25px_rgba(0,0,0,0.08)] border border-gray-200 rounded-2xl z-50 overflow-hidden"
                    >
                      {item.dropdown.map((sub) => (
                        <Link
                          key={sub.href}
                          href={sub.href}
                          className="block px-5 py-3 inter-medium text-[15px] text-gray-800 hover:bg-[#F7F8FA] hover:text-[#227abb] transition-all duration-200"
                        >
                          {sub.label}
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <Link
                key={item.href}
                href={item.href ?? "#"}
                className="text-sm font-semibold transition-all"
                style={{
                  color: currentLabelColor,
                }}
                onMouseEnter={(e) => {
                  (e.target as HTMLElement).style.color = currentHoverColor;
                }}
                onMouseLeave={(e) => {
                  (e.target as HTMLElement).style.color = currentLabelColor;
                }}
              >
                {item.label}
              </Link>
            )
          )}

          {/* ---------- CTA Button ---------- */}
          <Link
            href={ctaLink}
            className="ml-6 font-semibold text-sm px-5 py-2 rounded-full shadow-md transition-all duration-300"
            style={{
              backgroundColor: currentButtonBg,
              color: currentButtonText,
            }}
            onMouseEnter={(e) => {
              (e.target as HTMLElement).style.backgroundColor =
                currentButtonHoverBg;
              (e.target as HTMLElement).style.color = currentButtonHoverText;
            }}
            onMouseLeave={(e) => {
              (e.target as HTMLElement).style.backgroundColor = currentButtonBg;
              (e.target as HTMLElement).style.color = currentButtonText;
            }}
          >
            {ctaText}
          </Link>
        </nav>

        {/* ---------- Mobile Button ---------- */}
        <button
          className={`md:hidden ${
            isLight && !isScrolled ? "text-white" : "text-gray-900"
          }`}
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          {isMenuOpen ? <X size={24} /> : <RiMenu3Line size={24} />}
        </button>
      </motion.div>

      {/* ---------- Mobile Menu ---------- */}
      <AnimatePresence>
        {isMenuOpen && (
          <>
            {/* Overlay */}
            <motion.div
              key="overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.4 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={() => setIsMenuOpen(false)}
              className="fixed inset-0 bg-black z-49" // keeps it behind menu
            />

            {/* Drawer Menu */}
            <motion.div
              key="drawer"
              initial={{ x: "-100%", opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: "-100%", opacity: 0 }}
              transition={{
                type: "spring",
                stiffness: 100,
                damping: 18,
                duration: 0.4,
              }}
              className="fixed inset-y-0 left-0 w-[80%] sm:w-[65%] bg-white shadow-2xl z-50 overflow-y-auto h-screen backdrop-blur-xl rounded-r-2xl"
            >
              {/* Header with logo */}
              <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
                <img
                  src="/logo/color-full-logo.png"
                  alt="logo"
                  className="w-40"
                />
                <button
                  onClick={() => setIsMenuOpen(false)}
                  className="text-gray-600 hover:text-[#4177BC] transition-colors"
                >
                  <X size={26} />
                </button>
              </div>

              {/* Nav Items */}
              <div className="px-6 py-6 flex flex-col space-y-5">
                {navItems.map((item) =>
                  item.dropdown ? (
                    <details key={item.label} className="group">
                      <summary className="text-base  rounded  font-medium cursor-pointer text-gray-900 flex justify-between items-center">
                        {item.label}
                        <ChevronDown
                          size={16}
                          className="group-open:rotate-180 transition-transform"
                        />
                      </summary>
                      <div className="ml-4 mt-2   space-y-2">
                        {item.dropdown.map((sub) => (
                          <Link
                            key={sub.href}
                            href={sub.href}
                            onClick={() => setIsMenuOpen(false)}
                            className="block text-sm text-gray-700 inter-medium hover:text-[#227abb]"
                          >
                            {sub.label}
                          </Link>
                        ))}
                      </div>
                    </details>
                  ) : (
                    <Link
                      key={item.href}
                      href={item.href ?? "#"}
                      onClick={() => setIsMenuOpen(false)}
                      className="text-base font-medium text-gray-900 hover:text-[#227abb] transition-colors"
                    >
                      {item.label}
                    </Link>
                  )
                )}

                {/* CTA Button */}
                <Link
                  href={ctaLink}
                  onClick={() => setIsMenuOpen(false)}
                  className="font-semibold px-4 py-2 rounded-full text-center text-white mt-6 shadow-md"
                  style={{ backgroundColor: "#EB9C2C" }}
                >
                  {ctaText}
                </Link>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </header>
  );
}
